<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { SYSTEM_FIELDS } from 'dashboard/composables/useCsvColumnMapping';

const props = defineProps({
  mapping: {
    type: Object,
    required: true,
  },
  headers: {
    type: Array,
    required: true,
  },
  previewRows: {
    type: Array,
    required: true,
  },
});

const { t } = useI18n();

// Get mapped columns in system field order
const mappedColumns = computed(() => {
  const result = [];

  SYSTEM_FIELDS.forEach(field => {
    // Find which CSV header maps to this system field
    const csvHeader = Object.entries(props.mapping).find(
      ([, value]) => value === field.key
    );

    if (csvHeader) {
      const headerIndex = props.headers.indexOf(csvHeader[0]);
      result.push({
        systemField: field.key,
        label: t(field.labelKey),
        headerIndex,
      });
    }
  });

  return result;
});

// Get preview cell value
const getCellValue = (row, headerIndex) => {
  const value = row[headerIndex] || '';
  return value.length > 40 ? `${value.slice(0, 40)}...` : value;
};

const displayRowCount = computed(() =>
  Math.min(props.previewRows.length, 3)
);
</script>

<template>
  <div v-if="mappedColumns.length > 0" class="space-y-2">
    <div>
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('CONTACT_IMPORT.PREVIEW_TABLE.TITLE') }}
      </h3>
      <p class="text-xs text-n-slate-10 mt-0.5">
        {{
          t('CONTACT_IMPORT.PREVIEW_TABLE.DESCRIPTION', {
            count: displayRowCount,
          })
        }}
      </p>
    </div>

    <div class="border border-n-slate-6 rounded-lg overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-n-slate-2">
            <th
              v-for="col in mappedColumns"
              :key="col.systemField"
              class="px-3 py-2 text-left text-xs font-semibold text-n-slate-11 uppercase tracking-wide border-b border-n-slate-6"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, rowIndex) in previewRows.slice(0, 3)"
            :key="rowIndex"
            class="border-b border-n-slate-6 last:border-b-0"
          >
            <td
              v-for="col in mappedColumns"
              :key="`${rowIndex}-${col.systemField}`"
              class="px-3 py-2 text-n-slate-12 whitespace-nowrap"
            >
              {{ getCellValue(row, col.headerIndex) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <div
    v-else
    class="p-4 text-center text-sm text-n-slate-10 border border-dashed border-n-slate-6 rounded-lg"
  >
    {{ t('CONTACT_IMPORT.PREVIEW_TABLE.NO_MAPPED_COLUMNS') }}
  </div>
</template>
